class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.initialResultsMarkup = this.predictiveSearchResults.innerHTML;
    this.hasInitialSuggestions = Boolean(this.predictiveSearchResults.querySelector('[data-search-empty-state]'));
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  canShowResults() {
    const modal = this.closest('details-modal');
    return this.isConnected && this.contains(document.activeElement) && (!modal || modal.isOpen());
  }

  onChange() {
    super.onChange();
    if (!this.canShowResults()) return;
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('[id^="predictive-search-results-groups-wrapper"]')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.showInitialResults();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.showInitialResults();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showInitialResults();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.code === 'Escape' && this.isOpen) {
      event.preventDefault();
      if (!this.closest('details-modal')) event.stopPropagation();
      this.close();
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
    if (event.code === 'Enter' && this.isOpen && this.querySelector('[aria-selected="true"]')) {
      event.preventDefault();
      this.selectOption();
    }
  }

  onClick(event) {
    const termButton = event.target.closest('[data-search-term]');
    if (!termButton || !this.contains(termButton)) return;

    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.input.value = termButton.dataset.searchTerm.trim();
    this.input.form.requestSubmit();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.abortController.abort();
  }

  async getSearchResults(searchTerm) {
    if (!this.canShowResults()) return;
    const queryKey = searchTerm.toLowerCase();
    this.abortController.abort();
    const controller = this.abortController = new AbortController();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    if (Date.now() < (PredictiveSearch.suspendedUntil || 0)) {
      this.close();
      return;
    }
    this.setLiveRegionLoadingState();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
        signal: controller.signal,
      });
      if (response.status === 429) {
        const retry = response.headers.get('Retry-After')?.trim();
        const delay = retry && /^\d+$/.test(retry) ? Number(retry) * 1000 : Date.parse(retry) - Date.now();
        PredictiveSearch.suspendedUntil = Date.now() + (Number.isFinite(delay) && delay >= 0 ? delay : 60000);
      }
      if (!response.ok) throw new Error(`Search HTTP ${response.status}`);
      const text = await response.text();
      if (controller.signal.aborted || this.abortController !== controller || !this.isConnected || this.getQuery() !== searchTerm) return;
      const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
      if (!section?.querySelector('[data-predictive-search-live-region-count-value]') || text.includes('Liquid error')) {
        throw new Error('Resultados de busca incompletos');
      }
      this.allPredictiveSearchInstances.forEach((instance) => {
        instance.cachedResults[queryKey] = section.innerHTML;
      });
      if (this.contains(document.activeElement)) this.renderSearchResults(section.innerHTML);
    } catch (_) {
      // The regular search form remains usable if its enhancement fails.
      if (this.abortController === controller && this.isConnected) this.close();
    } finally {
      clearTimeout(timeout);
      if (this.abortController === controller) this.removeAttribute('loading');
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.canShowResults()) return;
    const fragment = document.createElement('template');
    fragment.innerHTML = resultsMarkup;
    const idMap = new Map();
    fragment.content.querySelectorAll('[id]').forEach((element) => {
      const scopedId = `${element.id}-${this.input.id}`;
      idMap.set(element.id, scopedId);
      element.id = scopedId;
    });
    fragment.content.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], [aria-owns]').forEach((element) => {
      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'].forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (value) element.setAttribute(attribute, value.split(' ').map((id) => idMap.get(id) || id).join(' '));
      });
    });
    this.predictiveSearchResults.replaceChildren(fragment.content);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  showInitialResults() {
    if (!this.canShowResults()) return;
    if (!this.hasInitialSuggestions) {
      this.close(true);
      return;
    }

    this.predictiveSearchResults.innerHTML = this.initialResultsMarkup;
    this.setAttribute('results', true);
    this.open();
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.predictiveSearchResults.removeAttribute('hidden');
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.cancelPendingInput();
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.predictiveSearchResults.setAttribute('hidden', '');
  }
}

customElements.define('predictive-search', PredictiveSearch);
